import { Flex, Heading, Button, FormControl, FormLabel, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Box, RadioGroup, Radio, Text } from '@chakra-ui/react'; 
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SettingsBreadcrumb from '../../../components/SettingsBreadcrumb';
import CustomInput from '../../../components/common/Input/input';
import * as api from '../../../api/redesSociais';
import Swal from 'sweetalert2';
import { HelpCircle } from 'lucide-react';
import HelpGuideModal from '../../../components/GuiaDeAjuda/GuiaDeAjuda';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookSquare, faInstagram, faLinkedin, faYoutube,
  faTiktok, faXTwitter, faWhatsapp, faTelegram, faTumblrSquare
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';

function EditarRedeSocial() {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isHelpModalOpen, setIsHelpModalOpen] = useState<boolean>(false);
  const location = useLocation();
  const { descricaoRede } = location.state;
  const { codigoRede } = location.state;
  const navigate = useNavigate();
  const { id } = useParams();

  const [formValues, setFormValues] = useState({
    dscUsuario: { value: '', error: false },
    dscTokenAcesso: { value: '', error: false },
    dscPaginaId: { value: '', error: false },
    sitRegistro: { value: 0, error: false },
    dscSenha: { value: '', error: false },
    dscEndpoint: { value: '', error: false },
    dscChaveApi: { value: '', error: false },
  });

  const tituloRede = (codigo: number) => {
    const titles: { [key: number]: string } = {
      0: 'Facebook',
      1: 'Instagram',
      2: 'LinkedIn',
      3: 'Youtube',
      4: 'TikTok',
      5: 'X (Antigo Twitter)',
      6: 'Whatsapp',
      7: 'Telegram',
      8: 'Tumblr',
      9: 'Página Internet',
    };

    return titles[codigo] || '';
  };

  const iconeRedeSocial = (id: number) => {
    const icones: { [key: number]: { icon: any, color: string } } = {
      0: { icon: faFacebookSquare, color: "#1789fc" },
      1: { icon: faInstagram, color: "#ec1393" },
      2: { icon: faLinkedin, color: "#1DA1F2" },
      3: { icon: faYoutube, color: "#ff0000" },
      4: { icon: faTiktok, color: "#080404" },
      5: { icon: faXTwitter, color: "#080404" },
      6: { icon: faWhatsapp, color: "#43e460" },
      7: { icon: faTelegram, color: "#088ccc" },
      8: { icon: faTumblrSquare, color: "#384c5c" },
    };

    return icones[id] || { icon: faGlobe, color: "#3b87d8" };
  };

  const fetchRedesSociais = async () => {
    if (!id) return;

    try {
      const data = await api.obterContaRedeSocial(Number(id));
      setFormValues({
        dscUsuario: { value: data.dscUsuario, error: false },
        dscTokenAcesso: { value: data.dscTokenAcesso, error: false },
        dscPaginaId: { value: data.dscPaginaId, error: false },
        sitRegistro: { value: data.sitRegistro, error: false },
        dscSenha: { value: data.dscSenha, error: false },
        dscEndpoint: { value: data.dscEndpoint, error: false },
        dscChaveApi: { value: data.dscChaveApi, error: false },
      });
    } catch (error) {
      console.error('Erro ao obter rede social:', error);
    }
  };

  useEffect(() => {
    fetchRedesSociais();
  }, [id]);

  const isFormValid = () => {
    const { dscUsuario, dscTokenAcesso, dscPaginaId } = formValues;

    // Campos obrigatórios básicos para todas as redes
    const camposBasicosValidos = dscUsuario.value?.trim() && dscTokenAcesso.value?.trim();

    // Para Instagram e Facebook, Page ID/User ID é obrigatório
    if (descricaoRede === 'Instagram' || descricaoRede === 'Facebook') {
      return camposBasicosValidos && dscPaginaId.value?.trim();
    }

    // Para outras redes (incluindo TikTok), apenas os campos básicos são obrigatórios
    return camposBasicosValidos;
  }; 

  const handleFieldChange = (field: any, value: any) => {
    setFormValues({
      ...formValues,
      [field]: { error: false, value }
    });
  };

  const handleSubmit = async () => {
    const { dscUsuario, dscTokenAcesso, sitRegistro, dscSenha, dscEndpoint, dscChaveApi, dscPaginaId } = formValues;

    const params = {
      dscUsuario: dscUsuario.value.trim() !== '' ? dscUsuario.value : null,
      dscTokenAcesso: dscTokenAcesso.value || null,
      dscPaginaId: dscPaginaId.value || null,
      sitRegistro: sitRegistro.value,
      dscSenha: dscSenha.value || null,
      dscEndpoint: dscEndpoint.value || null,
      dscChaveApi: dscChaveApi.value || null
    };
  
    try {
      await api.editarContaRedeSocial(Number(id), params);
      // setIsSuccessModalOpen(true);
      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        text: 'Rede Social editada com sucesso.',
        confirmButtonColor: '#1789FC',
        confirmButtonText: 'OK',
      }).then(() => {
        setIsSuccessModalOpen(false);
        navigate(-1)      
      });
    } catch (error) {
      console.error('Erro ao editar rede social:', error);

      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Erro ao editar rede social. Tente novamente mais tarde.',
        confirmButtonColor: '#1789FC',
        confirmButtonText: 'OK',
      }).then(() => {
        setIsSuccessModalOpen(false);
      });
    }
  };

  const openHelpModal = () => setIsHelpModalOpen(true);
  const closeHelpModal = () => setIsHelpModalOpen(false);

  return (
    <Flex p="40px" bgColor="#fafafa" direction="column" w="100%">
      <SettingsBreadcrumb />
      <Flex
      direction={{ base: "column", md: "row" }}
      alignItems="center"
      justifyContent="space-between"
      w="100%"
      mb="20px"
    >
      <Heading
        mt="30px"
        fontWeight="600"
        fontSize="21px"
        color="#6F6F6F"
      >
        Editar de configuração rede social
      </Heading>
      <Flex
        hidden={
          descricaoRede != 'Linkedin' &&
          descricaoRede != 'Instagram' && 
          descricaoRede != 'Facebook'
        }
        mt="30px"
        ml={{ base: 0, md: 4 }}
        alignItems="center"
        cursor="pointer"
        onClick={openHelpModal}
        _hover={{ color: "#1d4ed8" }}
      >
        <Text
          ml="0.5rem"
          color="#1789fc"
          fontSize="md"
          textDecoration="underline"
          display="inline-flex"
          alignItems="center"
          _hover={{
            color: '#1468C9',
          }}
        >
          <HelpCircle size={18} color="#1789fc" />
          <span style={{ marginLeft: "0.5rem" }}>
            Precisa de ajuda para configurar?
          </span>
        </Text>
      </Flex>
    </Flex>
    <Heading
        mb="25px"
        fontWeight="500"
        fontSize="21px"
        w="100%"
      >
        <FontAwesomeIcon
          style={{ marginRight: '10px', fontSize: '25px' }}
          icon={iconeRedeSocial(Number(codigoRede)).icon}
          color={iconeRedeSocial(Number(codigoRede)).color}
        />
        {`${tituloRede(Number(codigoRede))}`}
      </Heading>
      <div className="row mb-3">
        <div className="col-md-6 mb-4">
          <FormControl isRequired>
            <FormLabel mb="8px">Nome do Usuário</FormLabel>
            <CustomInput
              placeholder='Digite um usuário'
              value={formValues.dscUsuario.value}
              onChange={(e) => handleFieldChange('dscUsuario', e.target.value)}
              maxLength={60}
            />
          </FormControl>
        </div>
        {/* <div className="col-md-6 mb-4">
          <FormControl>
            <FormLabel mb="8px">Senha do Usuário</FormLabel>
            <CustomInput
              placeholder='Digite uma senha'
              type={'password'}
              value={formValues.dscSenha.value}
              onChange={(e) => handleFieldChange('dscSenha', e.target.value)}
              maxLength={60}
            />
          </FormControl>
        </div> */}
        <div hidden={descricaoRede === 'Instagram' || descricaoRede == 'Facebook'} className="col-md-6 mb-4">
          <FormControl>
            <FormLabel mb="8px">Url de redirecionamento autorizada para o aplicativo</FormLabel>
            <CustomInput
              placeholder='Digite um endpoint'
              value={formValues.dscEndpoint.value}
              onChange={(e) => handleFieldChange('dscEndpoint', e.target.value)}
              maxLength={60}
            />
          </FormControl>
        </div>
        <div className="col-md-6 mb-4">
          <FormControl isRequired>
            <FormLabel mb="8px">Token de Acesso</FormLabel>
            <CustomInput
              placeholder='Digite um token'
              value={formValues.dscTokenAcesso.value}
              onChange={(e) => handleFieldChange('dscTokenAcesso', e.target.value)}
              maxLength={512}
            />
          </FormControl>
        </div>
        {descricaoRede === 'Linkedin' && (
          <div className="col-md-6 mb-4">
            <FormControl>
              <FormLabel mb="8px">Página do Linkedin para publicação</FormLabel>
              <CustomInput
                placeholder="Digite uma página para publicação"
                value={formValues.dscPaginaId.value}
                onChange={(e) => handleFieldChange('dscPaginaId', e.target.value)}
                maxLength={60}
              />
            </FormControl>
          </div>
        )}
         {(descricaoRede === 'Instagram') && (
          <div className="col-md-6 mb-4">
            <FormControl isRequired>
              <FormLabel mb="8px">User ID</FormLabel>
              <CustomInput
                placeholder="Digite o user ID"
                value={formValues.dscPaginaId.value}
                onChange={(e) => handleFieldChange('dscPaginaId', e.target.value)}
                maxLength={150}
              />
            </FormControl>
          </div>
        )}
         {(descricaoRede === 'Facebook') && (
          <div className="col-md-6 mb-4">
            <FormControl isRequired>
              <FormLabel mb="8px">Page ID</FormLabel>
              <CustomInput
                placeholder="Digite o page ID"
                value={formValues.dscPaginaId.value}
                onChange={(e) => handleFieldChange('dscPaginaId', e.target.value)}
                maxLength={150}
              />
            </FormControl>
          </div>
        )}
        <div hidden={descricaoRede === 'Instagram' || descricaoRede == 'Facebook'} className="col-md-6 mb-4">
          <FormControl>
            <FormLabel mb="8px">Chave de API</FormLabel>
            <CustomInput
              placeholder='Digite uma chave'
              value={formValues.dscChaveApi.value}
              onChange={(e) => handleFieldChange('dscChaveApi', e.target.value)}
              maxLength={60}
            />
          </FormControl>
        </div>
        <div className="col-md-6 mb-4">
          <FormControl>
            <FormLabel mb="8px">Situação</FormLabel>
            <RadioGroup mt={3.5} onChange={(value) => handleFieldChange('sitRegistro', Number(value))} value={formValues.sitRegistro.value.toString()}>
              <Radio value="1" mr={5}>Ativo</Radio>
              <Radio value="0">Inativo</Radio>
            </RadioGroup>
          </FormControl>
        </div>
      </div>

      <Box alignItems={'flex-end'}>
        <Button m="px" onClick={() => navigate(-1)}>
          Desistir
        </Button>
        <Button
          m="6px"
          color="#FFF"
          bgColor="#1665D8"
          _hover={{ opacity: 0.8 }}
          onClick={handleSubmit}
          isDisabled={!isFormValid()}
        >
          Editar Rede Social
        </Button>
      </Box>

      <HelpGuideModal 
        isOpen={isHelpModalOpen} 
        onClose={closeHelpModal} 
        socialMedia={descricaoRede} 
      />
    </Flex>
  );
}

export default EditarRedeSocial;
